import React, { useState } from "react";
import styled from "styled-components";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import { sendSupportMessage } from "../services/api";

const FooterContainer = styled.footer`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  width: 100%;
  margin-top: auto;
  text-align: center;
  position: relative;
  gap: 0.5rem;
`;

const LinksContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FooterLink = styled.a`
  color: ${COLORS.SECONDARY};
  text-decoration: none;
  font-size: 0.7rem;
  position: relative;
  transition: color 0.2s;

  &:hover {
    color: ${COLORS.PRIMARY};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    color: white;
    
    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const Separator = styled.span`
  color: ${COLORS.SECONDARY};
  opacity: 0.6;
  font-size: 0.7rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    display: none;
  }
`;

const FooterCopyright = styled.span`
  font-size: 0.7rem;
  color: ${COLORS.SECONDARY};
  text-align: center;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    color: white;
  }
`;

const HelpModal = styled.div`
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #95B9E3;
  padding: 0.75rem 1.25rem;
  border-radius: 2rem;
  width: 220px;
  color: ${COLORS.SECONDARY};
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::after {
    content: "";
    position: absolute;
    bottom: -9px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #95B9E3;
  }

  ${FooterLink}:hover & {
    opacity: 1;
    visibility: visible;
  }

  p {
    font-size: 0.9rem;
    margin: 0 0 0.25rem 0;
    font-weight: normal;
  }
`;

const AskButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    text-decoration: underline;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 1rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: ${COLORS.SECONDARY};
  font-size: 1.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${COLORS.SECONDARY};
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;
  height: 3rem;

  .input-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 2.5rem;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    border-bottom: 1px solid #bad1e6;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: left;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    color: #080d44;
    font-size: 0.9rem;
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: 1rem;

  &:focus {
    outline: none;
  }
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    text-align: left;
    margin-right: 0;
    font-size: 0.9rem;
    width: 100%;
  }
`;

const Textarea = styled.textarea`
  padding: 0.25rem 0;
  border: none;
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  min-height: 150px;
  resize: vertical;
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: 1rem;

  &:focus {
    outline: none;
  }
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    text-align: left;
    margin-right: 0;
    font-size: 0.9rem;
    min-height: 100px;
  }
`;

const ErrorMessage = styled.div`
  color: ${COLORS.ERROR};
  font-size: 0.75rem;
  position: absolute;
  bottom: 0.3rem;
  right: 1rem;
  margin: 0;
  font-family: ${FONTS.SECONDARY};

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    bottom: -1.5rem;
    right: 0rem;
  }
`;

const SubmitButton = styled.button`
  background-color: #ff7f50;
  color: white;
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  border: none;
  border-radius: 2rem;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  align-self: center;
  margin-top: 1rem;

  &:hover {
    background-color: #e06542;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    width: 90%;
    padding: 1rem;
    border-radius: 2.5rem;
  }
`;

const LoginFooter = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    subject: "",
    message: ""
  });
  const [touched, setTouched] = useState({
    email: false,
    subject: false,
    message: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTouched({
      email: true,
      subject: true,
      message: true
    });
    
    // Comprobamos si hay campos vacíos
    if (!formData.email || !formData.subject || !formData.message) {
      return; // No continuamos si hay campos vacíos
    }
    
    try {
      console.log("Sending email:", formData);
      
      // Enviar mensaje de soporte usando el servicio API
      await sendSupportMessage({
        email: formData.email,
        subject: formData.subject,
        body: formData.message
      });
      
      console.log("Email sent successfully!");
      
      // Cerrar formulario y limpiar datos
      setShowContactForm(false);
      setFormData({ email: "", subject: "", message: "" });
      setTouched({ email: false, subject: false, message: false });
      setSubmitted(false);
      
      // Opcional: mostrar mensaje de éxito
      alert("Mensaje enviado correctamente. Te contactaremos pronto.");
      
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Error al enviar el mensaje. Por favor, inténtalo de nuevo.");
    }
  };

  // Determinar si mostrar el error para un campo
  const shouldShowError = (field) => {
    return (touched[field] || submitted) && !formData[field];
  };

  return (
    <FooterContainer>
      <LinksContainer>
        <FooterLink href="#" onClick={(e) => e.preventDefault()}>
          Ayuda
          <HelpModal>
            <p>¿Tienes alguna duda?</p>
            <AskButton onClick={() => setShowContactForm(true)}>
              PREGÚNTANOS
            </AskButton>
          </HelpModal>
        </FooterLink>
        <Separator> | </Separator>
        <FooterLink href="https://ofertasvinculantes.es/aviso-legal"
          target="_blank"
          rel="noopener noreferrer">
          Aviso legal
        </FooterLink>
        <Separator> | </Separator>
        <FooterLink href="https://ofertasvinculantes.es/condiciones-generales"
          target="_blank"
          rel="noopener noreferrer">
           Condiciones de contratación
        </FooterLink>
        <Separator> | </Separator>
        <FooterLink href="https://ofertasvinculantes.es/politica-de-privacidad"
          target="_blank"
          rel="noopener noreferrer">
          Política de privacidad
        </FooterLink>
        <Separator> | </Separator>
        <FooterLink href="https://ofertasvinculantes.es/politica-de-cookies"
          target="_blank"
          rel="noopener noreferrer">
           Política de cookies
        </FooterLink>
      </LinksContainer>
      <FooterCopyright>
        ©2025 Reservados todos los derechos
      </FooterCopyright>

      {showContactForm && (
        <Modal onClick={() => setShowContactForm(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Contacto</ModalTitle>
              <CloseButton onClick={() => setShowContactForm(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <ContactForm onSubmit={handleSubmit} noValidate>
              <FormGroup>
                <div className="input-row">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="ejemplo@correo.com"
                    required
                  />
                </div>
                {shouldShowError('email') && (
                  <ErrorMessage>Este campo es obligatorio</ErrorMessage>
                )}
              </FormGroup>
              <FormGroup>
                <div className="input-row">
                  <Label htmlFor="subject">Asunto</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>
                {shouldShowError('subject') && (
                  <ErrorMessage>Este campo es obligatorio</ErrorMessage>
                )}
              </FormGroup>
              <FormGroup style={{ height: 'auto', minHeight: '10rem' }}>
                <div className="input-row" style={{ height: 'auto', alignItems: 'flex-start' }}>
                  <Label htmlFor="message" style={{ paddingTop: '0.5rem' }}>Mensaje</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>
                {shouldShowError('message') && (
                  <ErrorMessage>Este campo es obligatorio</ErrorMessage>
                )}
              </FormGroup>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <SubmitButton type="submit">
                  Enviar
                </SubmitButton>
              </div>
            </ContactForm>
          </ModalContent>
        </Modal>
      )}
    </FooterContainer>
  );
};

export default LoginFooter;
